import React, { forwardRef } from "react";
import clsx from "clsx";

import { Button } from "components/ui";

import styles from "./primary-button.module.scss";

export type PrimaryButtonVariant = "primary" | "secondary";

export interface PrimaryButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: PrimaryButtonVariant;
  loading?: boolean;
  /**
   * Switches to white-on-dark styling for placement over a video / image
   * background (e.g. the intro screen). Primary fills white with charcoal
   * text; secondary becomes transparent with a white hairline border and
   * white text.
   */
  overlay?: boolean;
}

/**
 * WONE-471: thin wrapper around `components/ui/Button` that applies the
 * auth full-width pill styling. Lives here so auth screens have a
 * single import surface alongside `AuthLayout`.
 *
 * When `loading` is true, the underlying `Button`'s built-in loading mode
 * is bypassed — that mode hides the label and shows only a spinner, which
 * Vismay flagged as making the button look "empty / disappeared". We
 * render the label and a small spinner side-by-side instead, and mark the
 * button `disabled` so it can't be re-tapped.
 */
export const PrimaryButton = forwardRef<
  HTMLButtonElement,
  PrimaryButtonProps
>(
  (
    {
      variant = "primary",
      type = "button",
      loading = false,
      disabled,
      overlay = false,
      children,
      ...rest
    },
    ref
  ): JSX.Element => {
    const buttonVariant = overlay
      ? variant === "primary"
        ? "auth-overlay-primary"
        : "auth-overlay-secondary"
      : variant === "primary"
        ? "auth-primary"
        : "auth-secondary";

    return (
      <Button
        ref={ref}
        type={type}
        variant={buttonVariant}
        disabled={disabled || loading}
        {...rest}
      >
        <span className={styles["content"]}>
          {children}
          {loading && (
            <span
              data-testid="auth-button-spinner"
              aria-hidden="true"
              className={clsx("spinner-border", styles["spinner"])}
            />
          )}
        </span>
      </Button>
    );
  }
);

PrimaryButton.displayName = "PrimaryButton";

export default PrimaryButton;
